import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { useClinic, telHref } from "@/hooks/useClinic";
import { MapPin, Stethoscope, ArrowRight, ShieldCheck, Clock, Phone, Search } from "lucide-react";

export const LandingNav = () => {
  const clinic = useClinic();
  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-18 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-home-link">
          <Logo testid="nav-logo" />
          <div>
            <div className="font-head font-bold text-lg leading-none text-slate-900">{clinic.name}</div>
            <div className="text-[11px] text-slate-400">Clinic</div>
          </div>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/track" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-teal-700 px-2" data-testid="nav-track-link"><Search className="h-4 w-4" /> Track</Link>
          <Link to="/book"><Button data-testid="nav-book-link" className="bg-teal-600 hover:bg-teal-700 text-white">Book Online</Button></Link>
          <Link to="/login"><Button data-testid="staff-login-link" variant="outline">Staff Login</Button></Link>
        </div>
      </div>
    </header>
  );
};

export const LandingHero = ({ doctorCount }) => {
  const clinic = useClinic();
  return (
  <section className="relative overflow-hidden bg-[#0F172A] text-white">
    <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-teal-500/20 blur-3xl" />
    <div className="absolute -bottom-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-blue-500/10 blur-3xl" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 relative z-10 grid lg:grid-cols-2 gap-10 items-center">
      <div className="animate-fade-up">
        <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm text-teal-300 mb-6"><ShieldCheck className="h-4 w-4" /> Trusted Care</div>
        <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
          Meet the doctors who <span className="text-teal-400">care for you.</span>
        </h1>
        <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
          Expert specialists across cardiology, orthopedics, dermatology and general medicine — available at convenient locations across the city.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/book"><Button data-testid="hero-book-button" className="bg-teal-600 hover:bg-teal-700 text-white h-12 px-6 text-base">Book Appointment <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
          <a href="#doctors"><Button data-testid="view-doctors-button" variant="outline" className="h-12 px-6 text-base border-white/20 bg-white/5 text-white hover:bg-white/10">View Our Doctors</Button></a>
          <a href={telHref(clinic.phone)}><Button variant="outline" className="h-12 px-6 text-base border-white/20 bg-white/5 text-white hover:bg-white/10"><Phone className="h-4 w-4 mr-2" /> {clinic.phone}</Button></a>
        </div>
        <div className="mt-10 flex flex-wrap gap-8 text-sm text-slate-300">
          <div className="flex items-center gap-2"><Stethoscope className="h-4 w-4 text-teal-400" /> {doctorCount}+ specialists</div>
          <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-teal-400" /> Open 7 days</div>
          <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-teal-400" /> {clinic.address}</div>
        </div>
      </div>
      <div className="hidden lg:block">
        <img src="/shri-ji-logo.png" alt="Shri Ji Clinic logo" data-testid="hero-logo" className="rounded-3xl object-contain bg-white h-[460px] w-full shadow-2xl ring-1 ring-white/10 p-6" />
      </div>
    </div>
  </section>
  );
};

export const LandingCta = () => {
  const clinic = useClinic();
  return (
    <section className="bg-teal-600 text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
        <div>
          <h3 className="font-head text-2xl sm:text-3xl font-bold">Need to book a consultation?</h3>
          <p className="text-teal-100 mt-1">Book online in a minute, or call our front desk.</p>
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/book"><Button className="h-12 px-6 bg-white text-teal-700 hover:bg-teal-50 text-base font-semibold" data-testid="cta-book-button">Book Online</Button></Link>
          <a href={telHref(clinic.phone)}><Button variant="outline" className="h-12 px-6 border-white/40 text-white hover:bg-teal-700 text-base font-semibold bg-transparent"><Phone className="h-4 w-4 mr-2" /> Call {clinic.phone}</Button></a>
        </div>
      </div>
    </section>
  );
};

export const LandingFooter = () => {
  const clinic = useClinic();
  return <footer className="bg-[#0F172A] text-slate-400 py-8 text-center text-xs font-mono">© 2026 {clinic.name} • {clinic.address}</footer>;
};
